import type { Block, Condition, DeltaStyle, EnvelopeColumn, Grain, LensDocument, MeasureFormat, NumberStyle } from './types';

export type Shown = { delta: DeltaStyle; baseline: boolean; numbers: NumberStyle };

export function shownFor(block: Block, document: LensDocument): Shown {
  return {
    delta: block.delta ?? document.period.delta ?? 'pct',
    baseline: block.baseline ?? document.period.baseline ?? true,
    numbers: block.numbers ?? document.display.numbers ?? 'compact',
  };
}

type NumberOptions = { numbers?: NumberStyle; decimals?: number };

const MINUS = '−';
const sign = (value: number) => (value < 0 ? MINUS : '');

export function formatValue(value: number | null | undefined, format: MeasureFormat | undefined, options: NumberOptions = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  const size = Math.abs(value);
  const { numbers, decimals } = options;
  if (format === 'pct') {
    const magnitude = size * 100;
    const precision = magnitude === 0 || magnitude >= 10 ? 0 : magnitude >= 1 ? 1 : Math.min(100, 1 - Math.floor(Math.log10(magnitude)));
    return `${sign(value)}${magnitude.toFixed(decimals ?? (numbers === 'full' ? precision : size < 0.01 ? 2 : 1))}%`;
  }
  if (numbers === 'full' || (decimals !== undefined && size < 1e4)) {
    const digits = { minimumFractionDigits: decimals ?? 0, maximumFractionDigits: decimals ?? (format === 'num1' || format === 'days' || format === 'nights' ? 1 : format === 'num2' ? 2 : 0) };
    const symbol = format === 'aud' ? 'A$' : format === 'usd' ? 'US$' : '';
    return `${sign(value)}${symbol}${size.toLocaleString('en-AU', digits)}`;
  }
  if (format === 'aud' || format === 'usd') {
    const symbol = format === 'aud' ? 'A$' : 'US$';
    if (size >= 1e9) return `${sign(value)}${symbol}${(size / 1e9).toFixed(Math.max(decimals ?? (size >= 1e10 ? 1 : 2), 1))}B`;
    if (size >= 1e6) return `${sign(value)}${symbol}${(size / 1e6).toFixed(Math.max(decimals ?? (size >= 1e7 ? 1 : 2), 1))}M`;
    if (size >= 1e4) return `${sign(value)}${symbol}${(size / 1e3).toFixed(Math.max(decimals ?? 1, 1))}K`;
    if (size >= 10) return `${sign(value)}${symbol}${Math.round(size).toLocaleString('en-AU')}`;
    return `${sign(value)}${symbol}${size.toFixed(2)}`;
  }
  if (format === 'num1') return `${sign(value)}${size.toFixed(1)}`;
  if (format === 'num2') return `${sign(value)}${size.toFixed(2)}`;
  if (format === 'days' || format === 'nights') return `${sign(value)}${size.toFixed(1)}`;
  if (size >= 1e6) return `${sign(value)}${(size / 1e6).toFixed(1)}M`;
  return `${sign(value)}${Math.round(size).toLocaleString('en-AU')}`;
}

export function formatExact(value: number | null | undefined, format: MeasureFormat | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '–';
  if (format === 'aud' || format === 'usd')
    return `${sign(value)}${format === 'aud' ? 'A$' : 'US$'}${Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (format === 'pct') return `${(value * 100).toFixed(2)}%`;
  return value.toLocaleString('en-AU', { maximumFractionDigits: 2 });
}

export const unitOf = (format: MeasureFormat | undefined): string | undefined => (format === 'aud' ? 'A$' : format === 'usd' ? 'US$' : undefined);

export function unitName(format: MeasureFormat | undefined, color: string) {
  const unit = unitOf(format);
  return unit ? { name: unit, nameLocation: 'end' as const, nameGap: 6, nameTextStyle: { color, fontSize: 10, align: 'right' as const } } : {};
}

export function formatAxis(value: number, format: MeasureFormat | undefined): string {
  const size = Math.abs(value);
  if (format === 'pct') return `${+(value * 100).toFixed(2)}%`;
  if (size >= 1e6) return `${sign(value)}${+(size / 1e6).toFixed(2)}M`;
  if (size >= 1e3) return `${sign(value)}${+(size / 1e3).toFixed(2)}K`;
  return `${sign(value)}${+size.toFixed(size < 10 ? 1 : 0)}`;
}

const MAX_SHOWN_RISE = 9.99;

export type Delta = { text: string; arrow: '▲' | '▼' | '■'; tone: 'up' | 'down' | 'flat'; relative?: number };

export function delta(
  current: number | null | undefined,
  base: number | null | undefined,
  column: Pick<EnvelopeColumn, 'format' | 'lowerIsBetter' | 'decimals'>,
  style: DeltaStyle = 'pct',
  numbers?: NumberStyle,
): Delta | undefined {
  if (style === 'none' || current === null || current === undefined || base === null || base === undefined) return undefined;
  const difference = current - base;
  const arrow = difference > 0 ? '▲' : difference < 0 ? '▼' : '■';
  const tone = difference === 0 ? 'flat' : difference > 0 !== Boolean(column.lowerIsBetter) ? 'up' : 'down';
  if (column.format === 'pct')
    return {
      text: `${Math.abs(difference * 100).toFixed(column.decimals ?? (Math.abs(difference) < 0.001 ? 2 : 1))}pp`,
      arrow,
      tone,
      ...(base > 0 ? { relative: current / base - 1 } : {}),
    };
  const amount = formatValue(Math.abs(difference), column.format, { numbers, decimals: column.decimals });
  if (base === 0) return style === 'pct' ? (current > 0 ? { text: 'new', arrow, tone } : undefined) : { text: amount, arrow, tone };
  const relative = difference / Math.abs(base);
  const percent = relative > MAX_SHOWN_RISE && numbers !== 'full' ? '>999%' : `${Math.abs(relative * 100).toFixed(Math.abs(relative) < 0.1 ? 1 : 0)}%`;
  const signedAmount = `${difference < 0 ? MINUS : '+'}${amount}`;
  const text = style === 'diff' ? amount : style === 'both' ? `${percent} (${signedAmount})` : percent;
  return { text, arrow, tone, relative };
}

export const COMPARE_LABEL: Record<string, string> = { prior: 'vs prior', ly: 'vs last year', l2y: 'vs 2 years ago' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function shortDay(day: string, withYear = false): string {
  const [year, month, date] = day.slice(0, 10).split('-').map(Number);
  return `${date} ${MONTHS[month - 1]}${withYear ? ` ${year}` : ''}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function pointLabel(day: string, grain: Grain | undefined, withWeekday = false): string {
  if (day.includes('T')) return `${WEEKDAYS[new Date(`${day.slice(0, 10)}T00:00:00Z`).getUTCDay()]} ${shortDay(day)}, ${day.slice(11, 16)}`;
  const [year, month] = day.slice(0, 7).split('-').map(Number);
  if (grain === 'month') return `${MONTHS[month - 1]} ${year}`;
  if (grain === 'quarter') return `Q${Math.ceil(month / 3)} ${year}`;
  if (grain === 'year') return String(year);
  if (grain === 'week') return `w/c ${shortDay(day)}`;
  return withWeekday ? `${WEEKDAYS[new Date(`${day.slice(0, 10)}T00:00:00Z`).getUTCDay()]} ${shortDay(day)}` : shortDay(day);
}

export function monthOf(day: string): string {
  return MONTHS[Number(day.slice(5, 7)) - 1];
}

const SAID: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web', app: 'App', au: 'AU', nz: 'NZ', us: 'US', uk: 'UK', le: 'LE', b2b: 'B2B', sms: 'SMS', seo: 'SEO', sem: 'SEM', edm: 'EDM', cpc: 'CPC', luxuryescapes: 'Luxury Escapes', leagenthub: 'LE Agent Hub', lebusinesstraveller: 'LE Business Traveller', scoopon: 'Scoopon Travel', cudo: 'Cudo Travel', deals: 'Deals Travel', treatme: 'Treat Me Travel' };

const SMALL_WORDS = new Set(['a', 'an', 'and', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to']);

const capitalsSaid = (value: string) =>
  value
    .split(' ')
    .map((word, index) => (word.length <= 3 && !SMALL_WORDS.has(word.toLowerCase()) ? word : index === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase()))
    .join(' ');

export function conditionText(label: string, condition: Condition): string {
  if (Array.isArray(condition)) return `${label} is ${condition.map(memberLabel).join(', ')}`;
  if ('not' in condition) return `${label} isn’t ${condition.not.map(memberLabel).join(', ')}`;
  const [min, max] = [condition.min, condition.max].map((bound) => bound?.toLocaleString('en-AU'));
  if (min !== undefined && max !== undefined) return `${label} is ${min}–${max}`;
  return min !== undefined ? `${label} is at least ${min}` : `${label} is at most ${max}`;
}

export function memberLabel(value: string): string {
  if (/^\d{4}-\d{2}$/.test(value)) return `${monthOf(`${value}-01`)} ${value.slice(0, 4)}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return shortDay(value, true);
  if (/\s\|\s/.test(value)) return value.split(/\s\|\s/).map(memberLabel).join(' · ');
  if (/^[A-Z][A-Z0-9 &/'-]*$/.test(value) && /[A-Z]{4}/.test(value)) return capitalsSaid(value);
  if (!/^[a-z][a-z0-9 _-]*$/.test(value)) return value;
  return value
    .split(/[ _]+/)
    .map((word, index) =>
      SAID[word] ?? (index > 0 && SMALL_WORDS.has(word) ? word : word.length === 2 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)),
    )
    .join(' ');
}

export const humanise = (column: string) =>
  column
    .replace(/_(bk|id|key)$/i, '')
    .replace(/_/g, ' ')
    .replace(/^\w/, (letter) => letter.toUpperCase());

const PRESET_LABELS: Record<string, string> = {
  mtd: 'Month to date',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
  fytd: 'FY to date',
  last_week: 'Last week',
  last_month: 'Last month',
  last_quarter: 'Last quarter',
  last_year: 'Last year',
};

export function presetLabel(preset: string): string {
  const rolling = /^(\d+)([dw])$/.exec(preset);
  if (rolling) {
    const count = Number(rolling[1]);
    if (rolling[2] === 'd') return count === 1 ? 'Yesterday' : `${count} days`;
    return count === 1 ? '1 week' : `${count} weeks`;
  }
  const quarters = /^(\d+)q_back(\d+)$/.exec(preset);
  if (quarters) return `${quarters[1]} quarters${quarters[2] === '0' ? ', including this quarter' : `, ending ${quarters[2]} quarters ago`}`;
  const months = /^(\d+)m(?:_back(\d+))?$/.exec(preset);
  if (months?.[2]) return `${months[1]} months, ending ${Number(months[2]) - 1} months ago`;
  if (months) return Number(months[1]) === 1 ? 'Last month' : `${months[1]} months`;
  if (preset === 'all') return 'All time';
  const next = /^next_(\d+)([dwm])$/.exec(preset);
  if (next) return `Next ${next[1]} ${{ d: 'days', w: 'weeks', m: 'months' }[next[2] as 'd' | 'w' | 'm']}`;
  const timed = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})[^.]*\.\.(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(preset);
  if (timed) return [[timed[1], timed[2]], [timed[3], timed[4]]].map(([day, time]) => `${shortDay(day)}${time === '00:00' ? '' : `, ${time}`}`).join(' – ');
  const range = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(preset);
  if (range) return `${shortDay(range[1], range[1].slice(0, 4) !== range[2].slice(0, 4))} – ${shortDay(range[2], true)}`;
  return PRESET_LABELS[preset] ?? preset;
}
